/**
 * SchemaSourceTree — Tree view of source schema objects.
 * Collapsible categories (Tables, Procedures, Views, Indexes).
 * Click to select an object and view its DDL.
 * Checkboxes for selective convert/deploy.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, Table2, FileCode, Eye, Hash, Search, X } from 'lucide-react'

function SchemaSourceTree({ schema, selectedObject, onSelectObject, checkedObjects = new Set(), onToggleCheck, onToggleAllCategory, onContextMenu }) {
  const [expandedCategories, setExpandedCategories] = useState({
    tables: true,
    procedures: false,
    views: false,
    indexes: false,
  })

  const [expandedProcs, setExpandedProcs] = useState({})
  const [filterText, setFilterText] = useState('')

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }))
  }

  const toggleProcTable = (tableName) => {
    setExpandedProcs(prev => ({ ...prev, [tableName]: !prev[tableName] }))
  }

  if (!schema) {
    return (
      <div className="schema-tree-empty">
        Upload a DDL zip file to begin
      </div>
    )
  }

  // Group procedures by table name
  const procsByTable = {}
  for (const proc of schema.procedures) {
    const key = proc.tableName || '_ungrouped'
    if (!procsByTable[key]) procsByTable[key] = []
    procsByTable[key].push(proc)
  }

  const isSelected = (type, name) =>
    selectedObject?.type === type && selectedObject?.name === name

  const isChecked = (type, name) => checkedObjects.has(`${type}:${name}`)

  const isCategoryAllChecked = (type, items) =>
    items.length > 0 && items.every(item => checkedObjects.has(`${type}:${item.name}`))

  const isCategorySomeChecked = (type, items) =>
    items.some(item => checkedObjects.has(`${type}:${item.name}`))

  const checkedCountForCategory = (type, items) =>
    items.filter(item => checkedObjects.has(`${type}:${item.name}`)).length

  // Apply text filter
  const filterLower = filterText.toLowerCase()
  const filteredTables = filterText ? schema.tables.filter(t => t.name.toLowerCase().includes(filterLower)) : schema.tables
  const filteredProcs = filterText ? schema.procedures.filter(p => p.name.toLowerCase().includes(filterLower)) : schema.procedures
  const filteredViews = filterText ? schema.views.filter(v => v.name.toLowerCase().includes(filterLower)) : schema.views
  const filteredIndexes = filterText ? schema.indexes.filter(i => i.name.toLowerCase().includes(filterLower)) : schema.indexes

  // Re-group filtered procedures
  const filteredProcsByTable = {}
  for (const proc of filteredProcs) {
    const key = proc.tableName || '_ungrouped'
    if (!filteredProcsByTable[key]) filteredProcsByTable[key] = []
    filteredProcsByTable[key].push(proc)
  }

  return (
    <div className="schema-tree">
      {/* Filter bar */}
      <div className="schema-tree-filter">
        <Search size={12} className="schema-tree-filter-icon" />
        <input
          className="schema-tree-filter-input"
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          placeholder="Filter objects..."
        />
        {filterText && (
          <button className="schema-tree-filter-clear" onClick={() => setFilterText('')}>
            <X size={12} />
          </button>
        )}
      </div>
      {/* Tables */}
      <div className="schema-tree-category">
        <div className="schema-tree-category-header">
          <input
            type="checkbox"
            className="schema-tree-checkbox"
            checked={isCategoryAllChecked('table', schema.tables)}
            ref={el => { if (el) el.indeterminate = !isCategoryAllChecked('table', schema.tables) && isCategorySomeChecked('table', schema.tables) }}
            onChange={(e) => { e.stopPropagation(); onToggleAllCategory?.('table', schema.tables, e.target.checked) }}
          />
          <button className="schema-tree-category-toggle" onClick={() => toggleCategory('tables')}>
            {expandedCategories.tables ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Table2 size={14} />
            <span>Tables</span>
          </button>
          <span className="schema-tree-badge">
            {checkedCountForCategory('table', schema.tables)}/{filteredTables.length}
          </span>
        </div>
        {expandedCategories.tables && (
          <div className="schema-tree-items">
            {filteredTables.map(table => (
              <div
                key={table.name}
                className={`schema-tree-item ${isSelected('table', table.name) ? 'selected' : ''}`}
                onContextMenu={(e) => onContextMenu?.(e, 'table', table.name, table)}
              >
                <input
                  type="checkbox"
                  className="schema-tree-checkbox"
                  checked={isChecked('table', table.name)}
                  onChange={() => onToggleCheck?.('table', table.name)}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="schema-tree-item-label"
                  onClick={() => onSelectObject({ type: 'table', name: table.name, data: table })}
                >
                  {table.name}
                  <span className="schema-tree-item-meta">{table.columns.length} cols</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Procedures */}
      {filteredProcs.length > 0 && (
        <div className="schema-tree-category">
          <div className="schema-tree-category-header">
            <input
              type="checkbox"
              className="schema-tree-checkbox"
              checked={isCategoryAllChecked('procedure', schema.procedures)}
              ref={el => { if (el) el.indeterminate = !isCategoryAllChecked('procedure', schema.procedures) && isCategorySomeChecked('procedure', schema.procedures) }}
              onChange={(e) => { e.stopPropagation(); onToggleAllCategory?.('procedure', schema.procedures, e.target.checked) }}
            />
            <button className="schema-tree-category-toggle" onClick={() => toggleCategory('procedures')}>
              {expandedCategories.procedures ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <FileCode size={14} />
              <span>Procedures</span>
            </button>
            <span className="schema-tree-badge">
              {checkedCountForCategory('procedure', schema.procedures)}/{filteredProcs.length}
            </span>
          </div>
          {expandedCategories.procedures && (
            <div className="schema-tree-items">
              {Object.entries(filteredProcsByTable).map(([tableName, procs]) => (
                <div key={tableName}>
                  <button
                    className="schema-tree-subgroup"
                    onClick={() => toggleProcTable(tableName)}
                  >
                    {expandedProcs[tableName] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span>{tableName === '_ungrouped' ? 'Other' : tableName}</span>
                    <span className="schema-tree-badge-sm">{procs.length}</span>
                  </button>
                  {expandedProcs[tableName] && procs.map(proc => (
                    <div
                      key={proc.name}
                      className={`schema-tree-item schema-tree-item-nested ${isSelected('procedure', proc.name) ? 'selected' : ''}`}
                      onContextMenu={(e) => onContextMenu?.(e, 'procedure', proc.name, proc)}
                    >
                      <input
                        type="checkbox"
                        className="schema-tree-checkbox"
                        checked={isChecked('procedure', proc.name)}
                        onChange={() => onToggleCheck?.('procedure', proc.name)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <button
                        className="schema-tree-item-label"
                        onClick={() => onSelectObject({ type: 'procedure', name: proc.name, data: proc })}
                      >
                        {proc.operation || proc.name.split('_').pop()}
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Views */}
      {filteredViews.length > 0 && (
        <div className="schema-tree-category">
          <div className="schema-tree-category-header">
            <input
              type="checkbox"
              className="schema-tree-checkbox"
              checked={isCategoryAllChecked('view', schema.views)}
              ref={el => { if (el) el.indeterminate = !isCategoryAllChecked('view', schema.views) && isCategorySomeChecked('view', schema.views) }}
              onChange={(e) => { e.stopPropagation(); onToggleAllCategory?.('view', schema.views, e.target.checked) }}
            />
            <button className="schema-tree-category-toggle" onClick={() => toggleCategory('views')}>
              {expandedCategories.views ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <Eye size={14} />
              <span>Views</span>
            </button>
            <span className="schema-tree-badge">
              {checkedCountForCategory('view', schema.views)}/{filteredViews.length}
            </span>
          </div>
          {expandedCategories.views && (
            <div className="schema-tree-items">
              {filteredViews.map(view => (
                <div
                  key={view.name}
                  className={`schema-tree-item ${isSelected('view', view.name) ? 'selected' : ''}`}
                  onContextMenu={(e) => onContextMenu?.(e, 'view', view.name, view)}
                >
                  <input
                    type="checkbox"
                    className="schema-tree-checkbox"
                    checked={isChecked('view', view.name)}
                    onChange={() => onToggleCheck?.('view', view.name)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="schema-tree-item-label"
                    onClick={() => onSelectObject({ type: 'view', name: view.name, data: view })}
                  >
                    {view.name}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Indexes */}
      {filteredIndexes.length > 0 && (
        <div className="schema-tree-category">
          <div className="schema-tree-category-header">
            <input
              type="checkbox"
              className="schema-tree-checkbox"
              checked={isCategoryAllChecked('index', schema.indexes)}
              ref={el => { if (el) el.indeterminate = !isCategoryAllChecked('index', schema.indexes) && isCategorySomeChecked('index', schema.indexes) }}
              onChange={(e) => { e.stopPropagation(); onToggleAllCategory?.('index', schema.indexes, e.target.checked) }}
            />
            <button className="schema-tree-category-toggle" onClick={() => toggleCategory('indexes')}>
              {expandedCategories.indexes ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              <Hash size={14} />
              <span>Indexes</span>
            </button>
            <span className="schema-tree-badge">
              {checkedCountForCategory('index', schema.indexes)}/{filteredIndexes.length}
            </span>
          </div>
          {expandedCategories.indexes && (
            <div className="schema-tree-items">
              {schema.indexes.map(index => (
                <div
                  key={index.name}
                  className={`schema-tree-item ${isSelected('index', index.name) ? 'selected' : ''}`}
                  onContextMenu={(e) => onContextMenu?.(e, 'index', index.name, index)}
                >
                  <input
                    type="checkbox"
                    className="schema-tree-checkbox"
                    checked={isChecked('index', index.name)}
                    onChange={() => onToggleCheck?.('index', index.name)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    className="schema-tree-item-label"
                    onClick={() => onSelectObject({ type: 'index', name: index.name, data: index })}
                  >
                    {index.name}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SchemaSourceTree
